import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ErrorState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  ScrollText, Upload, Download, Search, MoreVertical, Pencil, RefreshCw, History, Archive, ArchiveRestore,
  FolderCog, Plus, Trash2, Info, Users, BellRing, FileSpreadsheet, FileText, CheckCircle2, AlertTriangle, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const FILE_TIMEOUT = 120000;
const ACCEPT = ".pdf,.doc,.docx,.odt,.png,.jpg,.jpeg,.webp";
const EMPLOYEE_TYPES = ["Permanent", "Contract", "Trainee", "Probationer", "Consultant"];
const EMPTY_SCOPE = { designation_ids: [], department_ids: [], employee_types: [] };
const EMPTY_FORM = {
  title: "", category: "", description: "", effective_date: "", requires_ack: true,
  ack_due_days: 7, scope: EMPTY_SCOPE, notify: true, file: null,
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS_CLS = {
  Accepted: "bg-emerald-100 text-emerald-800",
  Pending: "bg-amber-100 text-amber-800",
  Overdue: "bg-red-100 text-red-800",
  "No login": "bg-slate-200 text-slate-600",
  "Info only": "bg-slate-100 text-slate-600",
};

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors",
        active ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-teal")}>
      {children}
    </button>
  );
}

function scopeLabel(scope, desigs, depts) {
  const s = scope || {};
  const parts = [];
  const name = (list, id, f) => (list || []).find((x) => x.id === id)?.[f] || "?";
  if (s.employee_types?.length) parts.push(s.employee_types.join(" / "));
  if (s.department_ids?.length) parts.push(s.department_ids.map((id) => name(depts, id, "name")).join(" / "));
  if (s.designation_ids?.length) parts.push(s.designation_ids.map((id) => name(desigs, id, "title")).join(" / "));
  return parts.length ? parts.join(" · ") : "All employees";
}

function ScopePicker({ value, onChange, desigs, depts }) {
  const toggle = (key, id) => {
    const cur = value[key] || [];
    onChange({ ...value, [key]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const everyone = !value.designation_ids.length && !value.department_ids.length && !value.employee_types.length;
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-navy">Applies to: <span className="text-teal">{everyone ? "All employees" : "Selected groups only"}</span></p>
        {!everyone && <button type="button" className="text-xs text-teal hover:underline" onClick={() => onChange(EMPTY_SCOPE)}>Reset to everyone</button>}
      </div>
      <p className="text-[11px] text-slate-400 -mt-2">Leave everything unselected for all employees. If you pick in more than one row, an employee must match every row (e.g. Contract + Technician = contract technicians only).</p>
      <div>
        <Label className="text-xs text-slate-500">Employee type</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {EMPLOYEE_TYPES.map((t) => <Chip key={t} active={value.employee_types.includes(t)} onClick={() => toggle("employee_types", t)}>{t}</Chip>)}
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Department</Label>
        <div className="flex flex-wrap gap-1.5 mt-1 max-h-24 overflow-y-auto">
          {(depts || []).map((d) => <Chip key={d.id} active={value.department_ids.includes(d.id)} onClick={() => toggle("department_ids", d.id)}>{d.name}</Chip>)}
          {(depts || []).length === 0 && <span className="text-xs text-slate-400">No departments set up</span>}
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Designation</Label>
        <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto">
          {(desigs || []).map((d) => <Chip key={d.id} active={value.designation_ids.includes(d.id)} onClick={() => toggle("designation_ids", d.id)}>{d.title}</Chip>)}
          {(desigs || []).length === 0 && <span className="text-xs text-slate-400">No designations set up</span>}
        </div>
      </div>
    </div>
  );
}

function ComplianceBar({ c }) {
  if (!c) return null;
  const pct = c.total ? Math.round((c.accepted / c.total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-slate-500"><span className="font-semibold text-navy">{c.accepted}</span> / {c.total} accepted</span>
        <span className="flex gap-2">
          {c.overdue > 0 && <span className="text-red-600 font-semibold">{c.overdue} overdue</span>}
          {c.pending > 0 && <span className="text-amber-600">{c.pending} pending</span>}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={cn("h-full rounded-full", pct === 100 ? "bg-emerald-500" : c.overdue ? "bg-red-400" : "bg-teal")} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function PolicyLibrary() {
  const { isSuperAdmin } = useAuth();
  const qc = useQueryClient();
  const [activeCat, setActiveCat] = useState("");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [replaceItem, setReplaceItem] = useState(null);
  const [replace, setReplace] = useState({ file: null, change_note: "", effective_date: "", notify: true });
  const [historyItem, setHistoryItem] = useState(null);
  const [complianceItem, setComplianceItem] = useState(null);
  const [compFilter, setCompFilter] = useState("");
  const [catsOpen, setCatsOpen] = useState(false);
  const [newCat, setNewCat] = useState("");

  const cats = useQuery({ queryKey: ["policyCats"], queryFn: async () => (await api.get("/policies/categories")).data });
  const items = useQuery({
    queryKey: ["policies", showArchived],
    queryFn: async () => (await api.get("/policies", { params: { archived: showArchived } })).data,
  });
  const { data: desigs } = useQuery({ queryKey: ["designations"], queryFn: async () => (await api.get("/designations")).data });
  const { data: depts } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/departments")).data });
  const compliance = useQuery({
    queryKey: ["policyCompliance", complianceItem?.id],
    enabled: !!complianceItem,
    queryFn: async () => (await api.get(`/policies/${complianceItem.id}/compliance`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["policies"] });
    qc.invalidateQueries({ queryKey: ["policyCats"] });
    qc.invalidateQueries({ queryKey: ["policyCompliance"] });
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items.data || []).filter((i) =>
      (!activeCat || i.category === activeCat) &&
      (!q || [i.title, i.description, i.file_name, i.category].join(" ").toLowerCase().includes(q)));
  }, [items.data, activeCat, search]);

  const grouped = useMemo(() => {
    const order = (cats.data || []).map((c) => c.name);
    const map = new Map();
    for (const i of visible) { if (!map.has(i.category)) map.set(i.category, []); map.get(i.category).push(i); }
    return [...map.entries()].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
  }, [visible, cats.data]);

  const totals = useMemo(() => {
    const t = { policies: 0, overdue: 0, pending: 0 };
    for (const i of items.data || []) {
      if (!i.compliance) continue;
      t.policies += 1; t.overdue += i.compliance.overdue; t.pending += i.compliance.pending;
    }
    return t;
  }, [items.data]);

  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", form.file); fd.append("title", form.title.trim()); fd.append("category", form.category);
      fd.append("description", form.description); fd.append("effective_date", form.effective_date);
      fd.append("requires_ack", form.requires_ack); fd.append("ack_due_days", Number(form.ack_due_days) || 0);
      fd.append("scope", JSON.stringify(form.scope)); fd.append("notify", form.notify);
      return (await api.post("/policies", fd, { timeout: FILE_TIMEOUT })).data;
    },
    onSuccess: (res) => {
      toast.success(`Policy published${res.notified ? ` — ${res.notified} employee(s) notified` : ""}`);
      setUploadOpen(false); setForm(EMPTY_FORM); refresh();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const saveEdit = useMutation({
    mutationFn: async () => (await api.patch(`/policies/${editItem.id}`, {
      title: editForm.title.trim(), category: editForm.category, description: editForm.description,
      effective_date: editForm.effective_date, requires_ack: editForm.requires_ack,
      ack_due_days: Number(editForm.ack_due_days) || 0, scope: editForm.scope,
    })).data,
    onSuccess: () => { toast.success("Policy details updated"); setEditItem(null); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const doReplace = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", replace.file); fd.append("change_note", replace.change_note);
      fd.append("effective_date", replace.effective_date); fd.append("notify", replace.notify);
      return (await api.post(`/policies/${replaceItem.id}/replace`, fd, { timeout: FILE_TIMEOUT })).data;
    },
    onSuccess: (res) => {
      toast.success(`Published as version ${res.version} — everyone in scope must accept it again`);
      setReplaceItem(null); refresh();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const archive = useMutation({
    mutationFn: async (i) => (await api.delete(`/policies/${i.id}`)).data,
    onSuccess: () => { toast.success("Archived — employees no longer see it"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const restore = useMutation({
    mutationFn: async (i) => (await api.post(`/policies/${i.id}/restore`)).data,
    onSuccess: () => { toast.success("Restored"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remind = useMutation({
    mutationFn: async (i) => (await api.post(`/policies/${i.id}/remind`)).data,
    onSuccess: (res) => toast.success(res.sent ? `Reminder sent to ${res.sent} employee(s)` : "Nobody pending with a portal login"),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const addCat = useMutation({
    mutationFn: async () => (await api.post("/policies/categories", { name: newCat.trim() })).data,
    onSuccess: () => { toast.success("Category added"); setNewCat(""); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const delCat = useMutation({
    mutationFn: async (c) => (await api.delete(`/policies/categories/${c.id}`)).data,
    onSuccess: () => { toast.success("Category removed"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const saveBlob = async (url, params, fallbackName) => {
    const t = toast.loading("Preparing download…");
    try {
      const res = await api.get(url, { responseType: "blob", timeout: FILE_TIMEOUT, params });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = href; a.download = m ? m[1] : fallbackName; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 5000);
      toast.success("Downloaded", { id: t });
    } catch { toast.error("Unable to download — try again", { id: t }); }
  };

  const openEdit = (i) => {
    setEditItem(i);
    setEditForm({ ...EMPTY_FORM, title: i.title, category: i.category, description: i.description || "",
      effective_date: i.effective_date || "", requires_ack: i.requires_ack !== false,
      ack_due_days: i.ack_due_days ?? 7, scope: { ...EMPTY_SCOPE, ...(i.scope || {}) } });
  };
  const openUpload = () => { setForm({ ...EMPTY_FORM, category: activeCat || "" }); setUploadOpen(true); };

  const canUpload = form.title.trim() && form.category && form.file;
  const compRows = (compliance.data?.rows || []).filter((r) => !compFilter || r.status === compFilter);

  const detailsFields = (f, setF) => (<>
    <div><Label>Title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Travel & Conveyance Policy" /></div>
    <div className="grid grid-cols-2 gap-3">
      <div><Label>Category *</Label>
        <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
          <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
          <SelectContent>{(cats.data || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Effective from</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} /></div>
    </div>
    <div><Label>Short summary for employees</Label>
      <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="e.g. Rules for claiming travel, food allowance and local conveyance for camps and home collections." />
    </div>
    <div className="grid grid-cols-2 gap-3 items-end">
      <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
        <Switch checked={f.requires_ack} onCheckedChange={(v) => setF({ ...f, requires_ack: v })} /> Employees must accept
      </label>
      <div><Label>Days to accept</Label>
        <Input type="number" min={0} max={90} disabled={!f.requires_ack} value={f.ack_due_days}
          onChange={(e) => setF({ ...f, ack_due_days: e.target.value === "" ? "" : Math.max(0, Math.min(90, Number(e.target.value))) })} />
      </div>
    </div>
    <ScopePicker value={f.scope} onChange={(scope) => setF({ ...f, scope })} desigs={desigs} depts={depts} />
  </>);

  return (
    <div>
      <PageHeader overline="Awareness & Compliance" title="Policy Library" icon={ScrollText}
        subtitle="Publish company policies, make sure every employee has read and accepted them, and keep a record of it."
        actions={isSuperAdmin && <>
          <Button variant="outline" onClick={() => setCatsOpen(true)}><FolderCog className="h-4 w-4 mr-1" /> Categories</Button>
          <Button className="bg-navy" onClick={openUpload} data-testid="policy-upload-btn"><Upload className="h-4 w-4 mr-1" /> Publish Policy</Button>
        </>}
      />

      <div className="mb-5 flex items-start gap-3 rounded-lg border border-teal/30 bg-teal/5 px-4 py-3 text-sm text-slate-600">
        <Info className="h-4 w-4 mt-0.5 text-teal shrink-0" />
        <p>Employees see a red banner until they open each policy and tick <span className="font-medium">"I have read and understood"</span>. Each acceptance is recorded with the date, time, IP address and device. Once the deadline passes, the employee <span className="font-medium">cannot apply for leave or raise correction requests</span> until they accept. Attendance and grievances are never blocked. <span className="font-medium">Upload new version</span> makes everyone in scope accept again. Upload as <span className="font-medium">PDF</span> so it opens on phones.</p>
      </div>

      {!showArchived && totals.policies > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <Card className="p-4 shadow-none border-slate-200"><p className="overline text-slate-400">Active policies</p><p className="text-2xl font-semibold text-navy">{totals.policies}</p></Card>
          <Card className="p-4 shadow-none border-slate-200"><p className="overline text-slate-400">Acceptances pending</p><p className="text-2xl font-semibold text-amber-600">{totals.pending}</p></Card>
          <Card className="p-4 shadow-none border-slate-200"><p className="overline text-slate-400">Overdue (blocking)</p><p className="text-2xl font-semibold text-red-600">{totals.overdue}</p></Card>
        </div>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-center mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search policies…" className="pl-9 focus-visible:ring-teal" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 md:ml-auto">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} /> Show archived
        </label>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setActiveCat("")}
          className={cn("rounded-full border px-3 py-1 text-sm", !activeCat ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-teal")}>All</button>
        {(cats.data || []).map((c) => (
          <button key={c.id} onClick={() => setActiveCat(activeCat === c.name ? "" : c.name)}
            className={cn("rounded-full border px-3 py-1 text-sm", activeCat === c.name ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-teal")}>
            {c.name}{!showArchived && <span className="opacity-70"> ({c.count || 0})</span>}
          </button>
        ))}
      </div>

      {items.isLoading || cats.isLoading ? <Loading /> : items.isError ? (
        <ErrorState message="Couldn't load policies." onRetry={items.refetch} />
      ) : (items.data || []).length === 0 ? (
        showArchived ? <EmptyState icon={Archive} title="No archived policies" />
          : <EmptyState icon={ScrollText} title="No policies published yet"
              hint="Start with the Code of Conduct, Leave Policy and Grievance Mechanism. Every employee will be asked to read and accept them."
              action={isSuperAdmin && <Button className="bg-navy" onClick={openUpload}><Upload className="h-4 w-4 mr-1" /> Publish First Policy</Button>} />
      ) : visible.length === 0 ? (
        <EmptyState icon={Search} title="Nothing matches" action={<Button variant="outline" onClick={() => { setSearch(""); setActiveCat(""); }}>Clear filters</Button>} />
      ) : (
        <div className="space-y-8">
          {grouped.map(([cat, list]) => (
            <section key={cat}>
              <h2 className="overline text-slate-500 mb-3">{cat} <span className="text-slate-400">· {list.length}</span></h2>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {list.map((i) => (
                  <Card key={i.id} className={cn("border-slate-200 shadow-none p-4 flex flex-col gap-3 hover:border-teal/50", i.status === "Archived" && "opacity-70")} data-testid={`policy-${i.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="h-11 w-11 shrink-0 rounded-lg border bg-teal/5 border-teal/30 text-teal grid place-items-center"><FileText className="h-5 w-5" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-navy leading-snug">{i.title}</p>
                        <p className="text-xs text-slate-400">v{i.version} · {i.effective_date ? `Effective ${fmtDate(i.effective_date)}` : `Published ${fmtDate(i.published_at)}`}</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {i.status === "Archived" ? (
                            isSuperAdmin && <DropdownMenuItem onClick={() => restore.mutate(i)}><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</DropdownMenuItem>
                          ) : (<>
                            <DropdownMenuItem onClick={() => { setCompFilter(""); setComplianceItem(i); }}><Users className="h-4 w-4 mr-2" /> Who has accepted</DropdownMenuItem>
                            {i.requires_ack !== false && <DropdownMenuItem onClick={() => remind.mutate(i)}><BellRing className="h-4 w-4 mr-2" /> Remind pending employees</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => saveBlob(`/policies/${i.id}/download`, {}, i.file_name)}><Download className="h-4 w-4 mr-2" /> Download</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setHistoryItem(i)}><History className="h-4 w-4 mr-2" /> Version history</DropdownMenuItem>
                            {isSuperAdmin && <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openEdit(i)}><Pencil className="h-4 w-4 mr-2" /> Edit details & who it applies to</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setReplaceItem(i); setReplace({ file: null, change_note: "", effective_date: "", notify: true }); }}><RefreshCw className="h-4 w-4 mr-2" /> Upload new version</DropdownMenuItem>
                              <DropdownMenuItem className="text-red-600" onClick={() => window.confirm(`Archive "${i.title}"? Employees will stop seeing it. Existing acceptance records are kept.`) && archive.mutate(i)}>
                                <Archive className="h-4 w-4 mr-2" /> Archive
                              </DropdownMenuItem>
                            </>}
                          </>)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {i.description && <p className="text-sm text-slate-600 line-clamp-2">{i.description}</p>}
                    <p className="text-[11px] text-slate-500"><Users className="h-3 w-3 inline mr-1" />{scopeLabel(i.scope, desigs, depts)}
                      {i.requires_ack === false ? " · Read only, no acceptance needed" : ` · ${i.ack_due_days} day(s) to accept`}</p>
                    <div className="mt-auto pt-2 border-t border-slate-100">
                      {i.requires_ack !== false && i.compliance ? (
                        <button className="w-full text-left" onClick={() => { setCompFilter(""); setComplianceItem(i); }}><ComplianceBar c={i.compliance} /></button>
                      ) : <p className="text-[11px] text-slate-400">Updated {fmtDate(i.updated_at)} by {i.updated_by_name || i.updated_by}</p>}
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Publish */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Publish a policy</DialogTitle>
            <DialogDescription>Employees in scope are notified and asked to read and accept it.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            {detailsFields(form, setForm)}
            <div><Label>Policy document *</Label>
              <Input type="file" accept={ACCEPT} onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
              <p className="text-[11px] text-slate-400 mt-1">PDF recommended (opens directly on phones). Word or images also accepted · max 25 MB.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={form.notify} onCheckedChange={(v) => setForm({ ...form, notify: v })} /> Send an in-app notification to employees in scope</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button className="bg-navy" disabled={!canUpload || upload.isPending} onClick={() => upload.mutate()}>{upload.isPending ? "Publishing…" : "Publish"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit policy details</DialogTitle>
            <DialogDescription>Does not reset acceptances. To change the policy text itself, use "Upload new version".</DialogDescription></DialogHeader>
          <div className="space-y-4">{detailsFields(editForm, setEditForm)}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button className="bg-navy" disabled={!editForm.title.trim() || saveEdit.isPending} onClick={() => saveEdit.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New version */}
      <Dialog open={!!replaceItem} onOpenChange={(o) => !o && setReplaceItem(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Upload new version</DialogTitle>
            <DialogDescription>"{replaceItem?.title}" is currently v{replaceItem?.version}. Everyone it applies to will have to accept v{(replaceItem?.version || 0) + 1}. Earlier acceptances stay on record.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Revised document *</Label><Input type="file" accept={ACCEPT} onChange={(e) => setReplace({ ...replace, file: e.target.files?.[0] || null })} /></div>
            <div><Label>What changed? (shown to employees)</Label><Input value={replace.change_note} onChange={(e) => setReplace({ ...replace, change_note: e.target.value })} placeholder="e.g. Daily allowance for out-station camps revised" /></div>
            <div><Label>Effective from</Label><Input type="date" value={replace.effective_date} onChange={(e) => setReplace({ ...replace, effective_date: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={replace.notify} onCheckedChange={(v) => setReplace({ ...replace, notify: v })} /> Notify employees in scope</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceItem(null)}>Cancel</Button>
            <Button className="bg-navy" disabled={!replace.file || doReplace.isPending} onClick={() => doReplace.mutate()}>{doReplace.isPending ? "Uploading…" : `Publish v${(replaceItem?.version || 0) + 1}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={!!historyItem} onOpenChange={(o) => !o && setHistoryItem(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Version history</DialogTitle><DialogDescription>{historyItem?.title}</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {historyItem && [{ ...historyItem, current: true }, ...(historyItem.history || [])].map((v) => (
              <div key={v.version} className={cn("flex items-start gap-3 rounded-md border p-3", v.current ? "border-teal/40 bg-teal/5" : "border-slate-200")}>
                <div className="font-mono-i text-sm font-semibold text-navy w-8">v{v.version}</div>
                <div className="flex-1 min-w-0 text-sm">
                  <p className="text-slate-700">{v.change_note || "—"} {v.current && <span className="ml-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold">CURRENT</span>}</p>
                  <p className="text-[11px] text-slate-400 truncate">{v.file_name} · published {fmtDate(v.published_at)} · {v.updated_by_name || v.updated_by}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => saveBlob(`/policies/${historyItem.id}/download`, v.current ? {} : { version: v.version }, v.file_name)}><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Compliance */}
      <Dialog open={!!complianceItem} onOpenChange={(o) => !o && setComplianceItem(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{complianceItem?.title} · v{complianceItem?.version}</DialogTitle>
            <DialogDescription>Who has read and accepted the current version.</DialogDescription>
          </DialogHeader>
          {compliance.isLoading ? <Loading /> : compliance.isError ? <ErrorState onRetry={compliance.refetch} /> : compliance.data && (<>
            <div className="flex flex-wrap items-center gap-2">
              {[["", `All (${compliance.data.counts.total})`], ["Accepted", `Accepted (${compliance.data.counts.accepted})`],
                ["Pending", `Pending (${compliance.data.counts.pending})`], ["Overdue", `Overdue (${compliance.data.counts.overdue})`],
                ["No login", `No login (${compliance.data.counts.no_login})`]].map(([k, l]) => (
                <Chip key={k} active={compFilter === k} onClick={() => setCompFilter(k)}>{l}</Chip>
              ))}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => remind.mutate(complianceItem)} disabled={remind.isPending}><BellRing className="h-4 w-4 mr-1" /> Remind pending</Button>
                <Button size="sm" className="bg-teal hover:bg-teal/90 text-white" onClick={() => saveBlob(`/policies/${complianceItem.id}/compliance/export`, {}, "acknowledgements.xlsx")}><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
              </div>
            </div>
            <div className="overflow-auto border rounded-md mt-2 flex-1">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0">
                  <tr><th className="text-left p-2">Employee</th><th className="text-left p-2 hidden md:table-cell">Department / Designation</th>
                    <th className="text-left p-2">Status</th><th className="text-left p-2 hidden sm:table-cell">Opened</th><th className="text-left p-2">Accepted on</th></tr>
                </thead>
                <tbody>
                  {compRows.map((r) => (
                    <tr key={r.employee_id} className="border-t border-slate-100">
                      <td className="p-2"><p className="font-medium text-navy">{r.name}</p><p className="text-[11px] text-slate-400">{r.employee_code}</p></td>
                      <td className="p-2 hidden md:table-cell text-xs text-slate-500">{r.department || "—"}<br />{r.designation || "—"}</td>
                      <td className="p-2">
                        <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", STATUS_CLS[r.status])}>
                          {r.status === "Accepted" ? <CheckCircle2 className="h-3 w-3" /> : r.status === "Overdue" ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{r.status}
                        </span>
                        {r.status !== "Accepted" && r.due_date && <p className="text-[10px] text-slate-400 mt-0.5">due {fmtDate(r.due_date)}</p>}
                      </td>
                      <td className="p-2 hidden sm:table-cell text-xs text-slate-500">{r.first_viewed_at ? `${fmtDate(r.first_viewed_at)} (${r.view_count}×)` : "Not opened"}</td>
                      <td className="p-2 text-xs text-slate-600">{fmtDateTime(r.acknowledged_at)}{r.ack_ip && <p className="text-[10px] text-slate-400">IP {r.ack_ip}</p>}</td>
                    </tr>
                  ))}
                  {compRows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Nobody in this list.</td></tr>}
                </tbody>
              </table>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Categories */}
      <Dialog open={catsOpen} onOpenChange={setCatsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Policy categories</DialogTitle><DialogDescription>Only empty categories can be removed.</DialogDescription></DialogHeader>
          <div className="flex gap-2">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Social Media Policy" onKeyDown={(e) => e.key === "Enter" && newCat.trim() && addCat.mutate()} />
            <Button className="bg-navy" disabled={!newCat.trim() || addCat.isPending} onClick={() => addCat.mutate()}><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {(cats.data || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-slate-50 text-sm">
                <span className="text-slate-700">{c.name} <span className="text-slate-400">({c.count || 0})</span></span>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" disabled={c.count > 0}
                  onClick={() => window.confirm(`Remove category "${c.name}"?`) && delCat.mutate(c)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
